// SignInScreen.jsx — Google Identity Services gate.
//
// The GIS script is injected on demand rather than sitting in index.html: the
// app should not make a third-party request until sign-in is actually being
// attempted, and a blocked script then produces one clear error on one screen
// instead of a mysterious dead button.
//
// Error copy follows the backend's auth codes exactly (features/auth/deps.py's
// AuthFailure codes), plus `not_superuser`, which is this app's own gate: a
// perfectly valid Mono user who is not a superuser has no business here and
// deserves to be told that, not shown 403s.

import React from 'react';
import { GOOGLE_CLIENT_ID, decodeJwtPayload, signInWithGoogleCredential } from '../auth';
import { API_BASE } from '../config';
import { Icon, Note } from './primitives';

let gisPromise = null;
function loadGis() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true; s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => { gisPromise = null; reject(new Error('gis_load_failed')); };
    document.head.appendChild(s);
  });
  return gisPromise;
}

function errorCopy(code, email) {
  switch (code) {
    case 'not_superuser':
      return {
        title: 'Not a superuser',
        body: `${email || 'That account'} is a valid Mono user but not a superuser. The control center is superuser-only — use the main app instead.`,
      };
    case 'not_provisioned':
      return {
        title: 'Not provisioned',
        body: `${email || 'That email'} has no Mono account. Mono has no self-signup; an existing superuser must provision it first.`,
      };
    case 'email_conflict':
      return {
        title: 'Account conflict',
        body: 'This email is bound to a different Google account. Another superuser can clear the binding from Support → Unbind Google account.',
      };
    case 'inactive':
      return { title: 'Access revoked', body: 'This user or organisation has been deactivated.' };
    case 'sso_unconfigured':
      return { title: 'SSO not configured', body: 'The backend has no GOOGLE_CLIENT_ID set, so it cannot verify sign-ins yet.' };
    case 'db_unconfigured':
      return { title: 'Backend has no database', body: 'DATABASE_URL is unset on the backend. Nothing can sign in until it is configured.' };
    case 'invalid_refresh':
    case 'reuse_detected':
    case 'session_expired':
      return { title: 'Session ended', body: 'Your session is no longer valid. Sign in again to continue.' };
    case 'gis_load_failed':
      return { title: 'Google sign-in did not load', body: 'Check the network or an ad-blocker, then reload.' };
    case 'origin_not_allowed':
      return {
        title: 'Origin not authorised',
        body: `Google refused to render the button for ${window.location.origin}. Add that origin to the OAuth client's authorised JavaScript origins.`,
      };
    default:
      return code
        ? { title: 'Sign-in failed', body: `The backend rejected the sign-in (${code}).` }
        : null;
  }
}

export default function SignInScreen({ initialError = null }) {
  const slot = React.useRef(null);
  const [err, setErr] = React.useState(
    initialError ? { code: initialError.code, email: initialError.message } : null);
  const [busy, setBusy] = React.useState(false);
  const configured = !!GOOGLE_CLIENT_ID;

  React.useEffect(() => {
    if (!configured) return undefined;
    let cancelled = false;
    loadGis().then(() => {
      if (cancelled || !window.google?.accounts?.id || !slot.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async ({ credential }) => {
          setBusy(true); setErr(null);
          try {
            await signInWithGoogleCredential(credential);   // state flows via auth.js emit
          } catch (e) {
            setErr({ code: e.code || `http_${e.status || '?'}`,
                     email: decodeJwtPayload(credential)?.email || null });
          } finally {
            setBusy(false);
          }
        },
      });
      window.google.accounts.id.renderButton(slot.current,
        { theme: 'outline', size: 'large', text: 'signin_with', width: 300 });
    }).catch(() => { if (!cancelled) setErr({ code: 'gis_load_failed' }); });
    return () => { cancelled = true; };
  }, [configured]);

  const copy = err ? errorCopy(err.code, err.email) : null;

  return (
    <div className="centered">
      <div className="gate-card">
        <svg width="32" height="32" viewBox="0 0 22 22" fill="none" aria-hidden>
          <rect x="1.6" y="1.6" width="18.8" height="18.8" rx="3"
                stroke="var(--ink)" strokeWidth="1.2" />
          <rect x="6" y="6" width="10" height="10" rx="1.5" fill="var(--accent)" />
        </svg>
        <div>
          <div className="gate-title">Control Center</div>
          <div className="sub" style={{ marginTop: 5 }}>
            Superuser access only. Sign in with the Google account bound to your
            Mono superuser.
          </div>
        </div>

        {!configured ? (
          <Note tone="warn" title="Not configured">
            VITE_GOOGLE_CLIENT_ID is unset. Add it to <span className="mono">.env</span> and
            restart the dev server (or rebuild).
          </Note>
        ) : (
          <div className="gis-slot" ref={slot} style={{ opacity: busy ? 0.5 : 1 }} />
        )}

        {busy && <div className="mono dim" style={{ fontSize: 11 }}>Signing in…</div>}

        {copy && (
          <Note tone={err.code === 'not_superuser' ? 'warn' : 'danger'} title={copy.title}>
            <div style={{ textAlign: 'left' }}>{copy.body}</div>
          </Note>
        )}

        {/* The origin is shown, not just the API base, because the most common
            first-run failure is Google's `origin_mismatch` — and that error
            happens on Google's own page, where this app cannot put a message.
            Whoever lands back here needs the exact string to paste into the
            OAuth client's authorised JavaScript origins, character for
            character (localhost and 127.0.0.1 are different origins). */}
        <div className="mono dim" style={{ fontSize: 10, overflowWrap: 'anywhere' }}>
          <div>{API_BASE.replace(/^https?:\/\//, '')}</div>
          <div style={{ marginTop: 2 }}>origin {window.location.origin}</div>
        </div>
      </div>
    </div>
  );
}

export function BootSplash() {
  return (
    <div className="centered">
      <div className="col" style={{ alignItems: 'center', gap: 12 }}>
        <svg width="30" height="30" viewBox="0 0 22 22" fill="none" aria-hidden>
          <rect x="1.6" y="1.6" width="18.8" height="18.8" rx="3"
                stroke="var(--ink)" strokeWidth="1.2" />
          <rect x="6" y="6" width="10" height="10" rx="1.5" fill="var(--accent)" />
        </svg>
        <div className="row" style={{ gap: 8 }}>
          <span className="spinner" />
          <span className="mono dim" style={{ fontSize: 11 }}>Resuming session…</span>
        </div>
      </div>
    </div>
  );
}

export { Icon };
